import * as React from 'react';

import { cn } from 'lib/utils';
import { DollarSign, BadgeCent } from 'lucide-react';
import {
    AudioInputPopover,
    InsertMode,
} from 'components/ui/audio-input-popover';
import {
    AudioInputDialog,
    InsertAction,
} from 'components/ui/audio-input-dialog';

export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    variant?: 'default' | 'currency-dollar' | 'currency-cents';
    enableAudio?: boolean;
    audioMode?: 'popover' | 'dialog';
    transcriptionPostProcessing?: string | boolean;
    allowCustomPostProcessing?: boolean;
    onTranscriptionComplete?: (text: string) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type,
            variant = 'default',
            enableAudio = false,
            audioMode = 'popover',
            transcriptionPostProcessing = false,
            allowCustomPostProcessing = false,
            onTranscriptionComplete,
            ...props
        },
        ref,
    ) => {
        const [value, setValue] = React.useState(props.value || '');
        const inputRef = React.useRef<HTMLInputElement | null>(null);

        React.useEffect(() => {
            if (props.value !== undefined) {
                setValue(props.value);
            }
        }, [props.value]);

        const handleTranscription = (
            text: string,
            mode: InsertMode | InsertAction,
        ) => {
            const newValue =
                mode === 'replace'
                    ? text
                    : mode === 'append' || mode === 'insert'
                      ? `${value} ${text}`.trim()
                      : text;

            setValue(newValue);

            if (props.onChange) {
                const event = {
                    target: { value: newValue },
                } as React.ChangeEvent<HTMLInputElement>;
                props.onChange(event);
            }

            onTranscriptionComplete?.(text);
        };

        const postProcessing =
            typeof transcriptionPostProcessing === 'string'
                ? transcriptionPostProcessing
                : transcriptionPostProcessing === true
                  ? 'Clean up filler words and fix punctuation'
                  : null;

        const getInput = (extraClassName?: string) => (
            <input
                type={type}
                className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    enableAudio && 'pr-10',
                    className,
                    extraClassName,
                )}
                ref={(node) => {
                    inputRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                {...props}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    props.onChange?.(e);
                }}
                data-1p-ignore
            />
        );

        const renderWithAudio = (input: React.ReactNode) => {
            if (!enableAudio) return input;

            return (
                <div className="relative w-full">
                    {input}
                    <div className="absolute right-1 top-1/2 -translate-y-1/2">
                        {audioMode === 'popover' ? (
                            <AudioInputPopover
                                onTranscriptionComplete={handleTranscription}
                                postProcessing={postProcessing}
                                hasExistingText={!!value}
                                disabled={props.disabled}
                            />
                        ) : (
                            <AudioInputDialog
                                onTranscriptionComplete={handleTranscription}
                                postProcessing={postProcessing}
                                allowCustomPostProcessing={
                                    allowCustomPostProcessing
                                }
                                disabled={props.disabled}
                            />
                        )}
                    </div>
                </div>
            );
        };

        if (variant === 'default') {
            return renderWithAudio(getInput());
        }

        if (variant === 'currency-dollar') {
            return renderWithAudio(
                <div className="relative w-full justify-center">
                    <DollarSign className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-foreground" />
                    {getInput('pl-8')}
                </div>,
            );
        }

        if (variant === 'currency-cents') {
            return renderWithAudio(
                <div className="relative w-full justify-center">
                    <BadgeCent className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 transform text-foreground" />
                    {getInput('pl-8')}
                </div>,
            );
        }
    },
);
Input.displayName = 'Input';

export { Input };
